import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Lock, AlertTriangle } from 'lucide-react';

const PinModal = ({ isOpen, onClose, onVerify, title, description, loading = false }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!pin || pin.length < 4 || pin.length > 6 || !/^\d+$/.test(pin)) {
      setError('PIN must be 4-6 digits');
      return;
    }
    
    try {
      await onVerify(pin);
      setPin('');
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid PIN');
    }
  };

  const handleClose = () => {
    setPin('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md border-2 border-slate-200">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Lock className="w-5 h-5 text-blue-600" />
            {title || 'Enter Admin PIN'}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {description || 'This action requires PIN verification for security.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-2">
              Admin PIN
            </label>
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={pin}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setPin(val);
                setError('');
              }}
              placeholder="Enter 4-6 digit PIN"
              className="text-center text-lg tracking-widest"
              autoFocus
              data-testid="pin-input"
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-2"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading || pin.length < 4}
              data-testid="pin-confirm-btn"
            >
              {loading ? 'Verifying...' : 'Confirm'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PinModal;
